import {
    type RecursiveRecord
} from "../../types";
import {
    warnOnce
} from "../../utils/development";
import kitPaletteData from "../../variants/themes/default.json";

const LEGACY_THEME_KEYS: Record<string, string> = {
    constrastHigh: "contrastHigh",
    constrastMid: "contrastMid",
    constrastLow: "contrastLow"
};

const migrateLegacyKeys = (projectJSON: RecursiveRecord): RecursiveRecord => {
    const migrated: RecursiveRecord = {};

    Object.keys(projectJSON).forEach((key) => {
        const value = projectJSON[key] as string | RecursiveRecord;
        const newKey = LEGACY_THEME_KEYS[key];

        if(newKey) {
            warnOnce({
                message: "Theme: \"constrastHigh\", \"constrastMid\" and \"constrastLow\" are deprecated and will be removed in the next major version. Use \"contrastHigh\", \"contrastMid\" and \"contrastLow\" instead."
            });

            if(projectJSON[newKey] === undefined) {
                migrated[newKey] = value;
            }

            return;
        }

        migrated[key] = typeof value === "object" && value !== null && !Array.isArray(value) ? migrateLegacyKeys(value) : value;
    });

    return migrated;
};

const getKitChart = ({
    paletteKey,
    themeKey
}: {
    paletteKey?: keyof NCoreUIKit.PaletteKey | string;
    themeKey?: keyof NCoreUIKit.ThemeKey | string;
}): RecursiveRecord => {
    const kitPalettes = kitPaletteData.palettes as unknown as NCoreUIKit.ThemePalettes;
    const kitPalette = kitPalettes.find(p => p.name === paletteKey) ?? kitPalettes[0]!;
    const kitTheme = kitPalette.themes[themeKey as keyof typeof kitPalette.themes] ?? kitPalette.themes.dark ?? Object.values(kitPalette.themes)[0];

    return (kitTheme as unknown as NCoreUIKit.ThemeTokens).chart as unknown as RecursiveRecord;
};

const withKitChart = ({
    paletteKey,
    themeKey,
    theme
}: {
    paletteKey?: keyof NCoreUIKit.PaletteKey | string;
    themeKey?: keyof NCoreUIKit.ThemeKey | string;
    theme: NCoreUIKit.ThemeTokens;
}): NCoreUIKit.ThemeTokens => {
    if(theme.chart) {
        return theme;
    }

    return {
        ...theme,
        chart: getKitChart({
            paletteKey,
            themeKey
        }) as unknown as NCoreUIKit.ThemeTokens["chart"]
    };
};

const mergeCurrent = (defaultJSON: RecursiveRecord, projectJSON: RecursiveRecord) => {
    const projectKeys: Array<keyof typeof projectJSON> = Object.keys(projectJSON);

    const newJSON = JSON.parse(JSON.stringify(defaultJSON));

    projectKeys.forEach((pKey) => {
        const pValue = projectJSON[pKey];
        const dValue = defaultJSON[pKey];

        if(typeof pValue === "string") {
            newJSON[pKey] = pValue;
        } else {
            if(typeof dValue !== "string") {
                if(dValue) {
                    newJSON[pKey] = mergeCurrent(dValue, pValue as RecursiveRecord);
                } else {
                    newJSON[pKey] = pValue;
                }
            } else {
                newJSON[pKey] = dValue;
            }
        }
    });

    return newJSON;
};

export const mergeTheme = (
    defaultTheme: NCoreUIKit.ThemeTokens,
    projectThemeProp: NCoreUIKit.PartialThemeTokens
): NCoreUIKit.ThemeTokens => {
    const projectTheme = migrateLegacyKeys(projectThemeProp as unknown as RecursiveRecord) as unknown as NCoreUIKit.PartialThemeTokens;

    return {
        chart: mergeCurrent((defaultTheme.chart ?? getKitChart({})) as unknown as RecursiveRecord, (projectTheme.chart ?? {}) as unknown as RecursiveRecord),
        content: mergeCurrent(defaultTheme.content as unknown as RecursiveRecord, (projectTheme.content ?? {}) as unknown as RecursiveRecord),
        project: mergeCurrent(defaultTheme.project as unknown as RecursiveRecord, (projectTheme.project ?? {}) as unknown as RecursiveRecord),
        system: mergeCurrent(defaultTheme.system as unknown as RecursiveRecord, (projectTheme.system ?? {}) as unknown as RecursiveRecord),
        code: mergeCurrent(defaultTheme.code as unknown as RecursiveRecord, (projectTheme.code ?? {}) as unknown as RecursiveRecord)
    };
};

export const mergePalettes = (
    defaultPalettes: NCoreUIKit.ThemePalettes,
    projectPalettes: NCoreUIKit.ThemePalettes,
    activePalette: keyof NCoreUIKit.PaletteKey,
    activeTheme: keyof NCoreUIKit.ThemeKey
): NCoreUIKit.ThemeTokens => {
    const defaultPalette = defaultPalettes.find(p => p.name === activePalette);

    if(!defaultPalette) {
        const projectPalette = projectPalettes.find(p => p.name === activePalette);

        if(projectPalette) {
            const projectThemeKey = Object.keys(projectPalette.themes).find(t => t === activeTheme) as keyof typeof projectPalette.themes;
            const fallbackPalette = defaultPalettes[0]!;
            const fallbackTheme = withKitChart({
                theme: (fallbackPalette.themes[activeTheme] ?? fallbackPalette.themes.dark ?? Object.values(fallbackPalette.themes)[0]) as unknown as NCoreUIKit.ThemeTokens,
                paletteKey: fallbackPalette.name,
                themeKey: activeTheme
            });

            if(projectThemeKey) {
                const projectTheme = projectPalette.themes[projectThemeKey] as unknown as NCoreUIKit.ThemeTokens;

                return mergeTheme(fallbackTheme, projectTheme);
            }

            return mergeTheme(fallbackTheme, Object.values(projectPalette.themes)[0] as unknown as NCoreUIKit.ThemeTokens);
        } else {
            const fallbackP = defaultPalettes[0]!;
            return withKitChart({
                theme: (fallbackP.themes.dark ?? Object.values(fallbackP.themes)[0]) as unknown as NCoreUIKit.ThemeTokens,
                paletteKey: fallbackP.name,
                themeKey: "dark"
            });
        }
    }

    const projectPalette = projectPalettes.find(p => p.name === activePalette);

    if(projectPalette) {
        const defaultThemeKey = Object.keys(defaultPalette.themes).find(t => t === activeTheme) as keyof typeof defaultPalette.themes;
        let defaultTheme = defaultPalette.themes[defaultThemeKey];

        if(!defaultTheme) {
            defaultTheme = defaultPalette.themes.dark ?? Object.values(defaultPalette.themes)[0];
        }

        const projectThemeKey = Object.keys(projectPalette.themes).find(t => t === activeTheme) as keyof typeof projectPalette.themes;

        if(projectThemeKey) {
            const projectTheme = projectPalette.themes[projectThemeKey] as unknown as NCoreUIKit.ThemeTokens;

            return mergeTheme(withKitChart({
                theme: defaultTheme as unknown as NCoreUIKit.ThemeTokens,
                paletteKey: activePalette,
                themeKey: activeTheme
            }), projectTheme);
        }

        return withKitChart({
            theme: defaultTheme as unknown as NCoreUIKit.ThemeTokens,
            paletteKey: activePalette,
            themeKey: activeTheme
        });
    }

    const defaultThemeKey = Object.keys(defaultPalette.themes).find(t => t === activeTheme) as keyof typeof defaultPalette.themes;
    let activeT = defaultPalette.themes[defaultThemeKey];

    if(!activeT) {
        activeT = defaultPalette.themes.dark ?? Object.values(defaultPalette.themes)[0];
    }

    return withKitChart({
        theme: activeT as unknown as NCoreUIKit.ThemeTokens,
        paletteKey: activePalette,
        themeKey: activeTheme
    });
};
